/**
 * Error Boundary Component
 * 
 * Purpose: Catch JavaScript errors in component tree
 * Benefits:
 * - Prevents entire app from crashing
 * - Shows friendly error message to users
 * - Logs errors for debugging
 * - Provides recovery option
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */

import React from 'react';
import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console (in production, send to error tracking service)
        console.error('Error Boundary Caught:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo
        });

        // TODO: Send to error tracking service (Sentry, etc.)
        // logErrorToService(error, errorInfo);
    }

    handleReload = () => {
        // Reload the page
        window.location.reload();
    };

    handleReset = () => {
        // Reset error state
        this.setState({ 
            hasError: false, 
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        backgroundColor: '#f5f5f5',
                        padding: 3,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            maxWidth: 600,
                            width: '100%',
                            padding: 4,
                            textAlign: 'center',
                        }}
                    >
                        <ErrorOutlineIcon
                            sx={{
                                fontSize: 80,
                                color: 'error.main',
                                mb: 2,
                            }}
                        />

                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Oops! Something went wrong
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            We encountered an unexpected error. Don't worry, your data is safe.
                        </Typography>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    padding: 2,
                                    marginBottom: 3,
                                    textAlign: 'left',
                                    backgroundColor: '#fff3f3',
                                    border: '1px solid #ffcdd2',
                                }}
                            >
                                <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    <strong>Error:</strong> {this.state.error.toString()}
                                </Typography>
                                {this.state.errorInfo && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: 11,
                                            mt: 1,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {this.state.errorInfo.componentStack}
                                    </Typography>
                                )}
                            </Paper>
                        )}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleReload}
                                size="large"
                            >
                                Reload Page
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={this.handleReset}
                                size="large"
                            >
                                Try Again
                            </Button>
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                            If this problem persists, please contact support at support@miirapos.com
                        </Typography>
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

/**
 * Function component wrapper (for convenience)
 * 
 * @example
 * import { withErrorBoundary } from '@components/feedback/ErrorBoundary';
 * 
 * const MyComponent = () => { ... };
 * 
 * export default withErrorBoundary(MyComponent);
 */
export const withErrorBoundary = (Component) => {
    return (props) => (
        <ErrorBoundary>
            <Component {...props} />
        </ErrorBoundary>
    );
};
